/**
 * Relink rules as pure functions (M3 plan R19, spec §6.3, D-TBD 13): repair lists, missing parts, the reboot's spawn
 * schedule, the guardian rule (a tetherstone vein within N relay hops) and when allied patrols may join. No state,
 * no ECS; the subsystem calls them on rare events.
 */
import type { FactionsConfig } from '../galaxy/layout/config'
import type { FactionKey } from '../galaxy/layout/types'
import type { Galaxy, RegionId } from '../galaxy/types'
import type { ReachSpawnKind, SpawnShipSpec } from '../reach/runtime/types'
import { PART_IDS, type PartId } from '../reach/types'
import type { RelinkConfig } from './config'

/** Part names in UI text. */
export const PART_LABEL: Readonly<Record<PartId, string>> = { hull_plating: 'hull plating', electronics: 'electronics', drive_parts: 'drive parts' }

/** Each part's slot in per-part arrays (`PART_IDS` order). */
export const PART_SLOT: Readonly<Record<PartId, number>> = { hull_plating: 0, electronics: 1, drive_parts: 2 }

/**
 * The repair list of a relay repaired from a system in `region`: `parts[region]`, times `longRelayMult` for a long
 * relay, each rounded up.
 */
export function repairList(long: boolean, region: RegionId, cfg: RelinkConfig): Record<PartId, number> {
  const base = cfg.parts[region]
  const mult = long ? cfg.longRelayMult : 1
  return {
    hull_plating: Math.ceil(base.hull_plating * mult),
    electronics: Math.ceil(base.electronics * mult),
    drive_parts: Math.ceil(base.drive_parts * mult),
  }
}

/** What is still missing: required − delivered per part, never below 0. */
export function missingParts(required: Readonly<Record<PartId, number>>, delivered: Readonly<Partial<Record<PartId, number>>>): Record<PartId, number> {
  const out: Record<PartId, number> = { hull_plating: 0, electronics: 0, drive_parts: 0 }
  for (const part of PART_IDS) out[part] = Math.max(0, required[part] - (delivered[part] ?? 0))
  return out
}

/**
 * Relay hops from `from` to every system within `maxHops` (BFS over every relay, whatever its state: guardians know
 * the old network). `from` itself is at 0.
 */
export function relayHops(galaxy: Galaxy, from: string, maxHops: number): Map<string, number> {
  const neighbours = new Map<string, string[]>()
  for (const r of galaxy.relays) {
    let a = neighbours.get(r.a)
    if (a === undefined) neighbours.set(r.a, (a = []))
    a.push(r.b)
    let b = neighbours.get(r.b)
    if (b === undefined) neighbours.set(r.b, (b = []))
    b.push(r.a)
  }
  const hops = new Map<string, number>([[from, 0]])
  let frontier = [from]
  for (let hop = 1; hop <= maxHops && frontier.length > 0; hop++) {
    const next: string[] = []
    for (const id of frontier) {
      for (const n of neighbours.get(id) ?? []) {
        if (hops.has(n)) continue
        hops.set(n, hop)
        next.push(n)
      }
    }
    frontier = next
  }
  return hops
}

/** The guardian rule: a tetherstone-vein system within `hops` relay hops of `systemId` (itself included). */
export function veinWithinHops(galaxy: Galaxy, systemId: string, hops: number): boolean {
  const near = relayHops(galaxy, systemId, hops)
  for (const s of galaxy.systems) if (s.archetype === 'tetherstone-vein' && near.has(s.id)) return true
  return false
}

/** Allied patrols may join a reboot only when the system's owner patrols (`factions.json` `patrols`). */
export function ownerPatrols(owner: FactionKey | null, factions: FactionsConfig): boolean {
  return owner !== null && factions.factions[owner]?.patrols === true
}

/** One scheduled reboot spawn: when (seconds into the reboot), who, and as what. */
export interface ScheduledSpawn {
  readonly atS: number
  readonly ships: readonly SpawnShipSpec[]
  readonly kind: Extract<ReachSpawnKind, 'rebootRaider' | 'guardian'>
}

/**
 * The reboot's hostile spawns in time order: the region's raider waves, plus the guardians at `atFraction` of
 * `durationS` when they join (a raider wave keeps its place on a tie). Each entry is one numbered wave on the HUD.
 */
export function rebootSchedule(region: RegionId, durationS: number, guardians: boolean, cfg: RelinkConfig): ScheduledSpawn[] {
  const out: ScheduledSpawn[] = cfg.reboot.waves[region].map((w) => ({ atS: w.atS, ships: w.ships, kind: 'rebootRaider' as const }))
  const g = cfg.reboot.guardians
  if (guardians && g.ships.length > 0) out.push({ atS: g.atFraction * durationS, ships: g.ships, kind: 'guardian' })
  // Array.prototype.sort is stable: raider waves stay ahead of guardians at the same time.
  return out.sort((a, b) => a.atS - b.atS)
}

/** "2 hull plating + 1 electronics" (parts with a quantity, in `PART_IDS` order); "nothing" when there are none. */
export function partsText(parts: Readonly<Partial<Record<PartId, number>>>): string {
  const bits: string[] = []
  for (const part of PART_IDS) {
    const n = parts[part] ?? 0
    if (n > 0) bits.push(`${n} ${PART_LABEL[part]}`)
  }
  return bits.length > 0 ? bits.join(' + ') : 'nothing'
}

/** Why a ship can't use a repair port (M3 port rules): too far, or too fast; null when it can. */
export type PortRefusal = 'range' | 'speed'

/**
 * The M3 repair-port rule (`portRangeM`, `portMaxSpeed`) from a squared distance and a squared speed (so callers
 * never take a square root). M7 S40's derelict repair port reuses it; `slow` = the speed limit applies (deliveries
 * and the reboot's start, not the port's prompt range). Pure and allocation-free.
 */
export function portRefusal(dist2: number, speed2: number, slow: boolean, cfg: Pick<RelinkConfig, 'portRangeM' | 'portMaxSpeed'>): PortRefusal | null {
  if (dist2 > cfg.portRangeM * cfg.portRangeM) return 'range'
  if (slow && speed2 > cfg.portMaxSpeed * cfg.portMaxSpeed) return 'speed'
  return null
}
